import React, { createContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { translations } from './translations';

export const I18nContext = createContext();

// Supported languages
const SUPPORTED_LOCALES = ['de', 'en'];
const FALLBACK_LOCALE = 'de';

// Get initial locale from URL or browser preferences
function getInitialLocale(pathname) {
  // First check URL path
  if (pathname.startsWith('/en')) {
    return 'en';
  }
  if (pathname.startsWith('/de')) {
    return 'de';
  }

  // Check if user has manually selected a language (stored in localStorage)
  const savedLocale = localStorage.getItem('busagent-locale');
  if (savedLocale && SUPPORTED_LOCALES.includes(savedLocale)) {
    return savedLocale;
  }

  // Otherwise, detect from browser preferences
  const browserLanguages = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];

  for (const lang of browserLanguages) {
    if (!lang) continue;

    // Extract base language code (e.g., 'de-DE' -> 'de')
    const baseLocale = lang.toLowerCase().split('-')[0];

    if (SUPPORTED_LOCALES.includes(baseLocale)) {
      return baseLocale;
    }
  }

  // Fallback to default
  return FALLBACK_LOCALE;
}

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [locale, setLocaleState] = useState(() => getInitialLocale(location.pathname));

  // Auto-redirect to language URL if no language prefix exists
  useEffect(() => {
    const currentPath = location.pathname;

    // Check if URL already has language prefix
    if (!currentPath.startsWith('/en') && !currentPath.startsWith('/de')) {
      // Get detected locale
      const detectedLocale = getInitialLocale(currentPath);

      // Redirect to language-prefixed URL
      const newPath = `/${detectedLocale}${currentPath === '/' ? '' : currentPath}`;
      navigate(newPath, { replace: true });
    }
  }, []);

  // Sync locale with URL changes
  useEffect(() => {
    const newLocale = getInitialLocale(location.pathname);
    if (newLocale !== locale) {
      setLocaleState(newLocale);
    }
  }, [location.pathname]);

  const setLocale = (newLocale) => {
    setLocaleState(newLocale);
    // Save manual selection to localStorage
    localStorage.setItem('busagent-locale', newLocale);

    // Update URL to match language
    const currentPath = location.pathname;
    let newPath;

    if (currentPath.startsWith('/en') || currentPath.startsWith('/de')) {
      // Replace existing language prefix
      newPath = currentPath.replace(/^\/(en|de)/, `/${newLocale}`);
    } else {
      // Add language prefix
      newPath = `/${newLocale}${currentPath}`;
    }

    navigate(newPath);
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[locale];

    for (const k of keys) {
      value = value?.[k];
    }

    return value || key;
  };

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
}
